import { MDBBtn, MDBCol, MDBContainer, MDBRow } from "mdb-react-ui-kit";
import {
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { BounceLoader } from "react-spinners";
import { TourOption } from "typings/mdb-react-ui-kit";
import _ from "lodash";
import TagManager from "react-gtm-module";
import {
    createGA4ItemFromTour,
    viewItemList,
} from "@/utils/analytics/tagmanager";
import { sortByPriority } from "@/utils/common/sortByPriority";
import { FilterResponseTO, SearchResponseTO } from "../../models/search";
import styles from "../../styles/search/Search.Search.module.scss";
import { randomUid } from "../../utils/randomIds";
import userInterfaceSvg from "../../utils/svg-icon/userInterface";
import TourDestinationsCard from "./TourDestinationsCard";
import TourDestinationsFiltersRecap from "./TourDestinationsFiltersRecap";
import TourDestinationsFilters from "./TourDestinationsFilters";
import ToursDestinationsNoResults from "./TourDestinationNoResults";
import shoppingSvg from "../../utils/svg-icon/shopping";
import TourDestinationsModalFilters from "./TourDestinationsModalFilters";
import { useIsMD } from "../../utils/hooks/useWindowSizes";
import { getFilterPackageData } from "../../utils/queries";
import { SearchSort } from "../../models/common/enums";
import { useCurrency } from "../../utils/hooks/useCurrency";
import useNavBarData from "../../utils/queries/hooks/useNavBarData";
import { useExchangeRates } from "../../utils/hooks/useExchangeRates";
import {
    convertPrice,
    convertToEur,
} from "../../utils/common/currencyConverter";
import { useDebounce } from "../../utils/hooks/useDebounce";

const ToursDestinationsSearch: FC<{
    filtersData: FilterResponseTO;
    packageData: SearchResponseTO[];
    setNumberFilteredPackage: Dispatch<SetStateAction<number>>;
    initialSelectedDestination: string[];
    initialSelectedTourType: string[];
    initialSelectedTagDestination: string | null;
}> = ({
    filtersData,
    packageData,
    setNumberFilteredPackage,
    initialSelectedDestination,
    initialSelectedTourType,
    initialSelectedTagDestination,
}) => {
    const [tourOptionToFilter, setTourOptionToFilter] =
        useState<TourOption>(undefined);

    const [selectedDestinations, setSelectedDestinations] = useState<string[]>(
        initialSelectedDestination
    );
    const [selectedTagDestination, setSelectedTagDestination] = useState<
        string | null
    >(initialSelectedTagDestination);
    const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
    // Parsing helper per prezzi, restituisce null se non valido
    const parsePriceValue = useCallback(
        (value?: number | string | null): number | null => {
            if (value === undefined || value === null) return null;
            const numeric =
                typeof value === "string" ? parseFloat(value) : value;
            if (Number.isNaN(numeric)) return null;
            return numeric;
        },
        []
    );

    // Estrae il prezzo massimo per un singolo tour
    const getTourMaxPrice = useCallback(
        (tour: SearchResponseTO): number => {
            // eslint-disable-next-line no-underscore-dangle
            const qualityPrices = tour.quality?._eurPrices;
            // eslint-disable-next-line no-underscore-dangle
            const premiumPrices = tour.premium?._eurPrices;

            const qualityPrice = parsePriceValue(
                qualityPrices?.startingPrice ?? qualityPrices?.price
            );
            const premiumPrice = parsePriceValue(
                premiumPrices?.startingPrice ?? premiumPrices?.price
            );

            return Math.max(qualityPrice ?? 0, premiumPrice ?? 0);
        },
        [parsePriceValue]
    );

    // Calcola il prezzo massimo reale dai tour disponibili (prima dell'inizializzazione dello stato)
    const calculateMaxPriceFromTours = useCallback(
        (tours: SearchResponseTO[]): number => {
            if (!tours || tours.length === 0) {
                return +filtersData.maxPrice;
            }

            const maxPrice = tours.reduce((currentMax, tour) => {
                const tourMax = getTourMaxPrice(tour);
                return tourMax > currentMax ? tourMax : currentMax;
            }, 0);

            return maxPrice > 0 ? maxPrice : +filtersData.maxPrice;
        },
        [filtersData.maxPrice, getTourMaxPrice]
    );

    // Salva i prezzi in EUR (valori base)
    // Inizialmente: min = 0, max = prezzo massimo disponibile
    const [selectedPriceEur, setSelectedPriceEur] = useState<number>(() => {
        return calculateMaxPriceFromTours(packageData);
    });
    const [selectedMinPriceEur, setSelectedMinPriceEur] = useState<number>(0);
    const [selectedTourType, setSelectedTourType] = useState<string[]>(
        initialSelectedTourType
    );

    const { currency } = useCurrency();
    const { data: navBarData } = useNavBarData();
    const { rates } = useExchangeRates();

    // Calcola il prezzo massimo reale dai tour disponibili (aggiornato quando i dati cambiano)
    const actualMaxPriceEur = useMemo(() => {
        return calculateMaxPriceFromTours(packageData);
    }, [packageData, calculateMaxPriceFromTours]);

    // Traccia se l'utente ha modificato esplicitamente il prezzo massimo
    const [hasUserModifiedMaxPrice, setHasUserModifiedMaxPrice] =
        useState(false);
    const isInitializingRef = useRef(true);

    // Inizializza selectedPriceEur con il max reale solo la prima volta
    useEffect(() => {
        if (isInitializingRef.current && actualMaxPriceEur > 0) {
            setSelectedPriceEur(actualMaxPriceEur);
            isInitializingRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualMaxPriceEur]);

    // Converti i prezzi da EUR alla valuta selezionata per la visualizzazione
    // displayMinPrice è sempre 0 (inizialmente)
    const displayMinPrice = useMemo(() => {
        return 0;
    }, []);

    // displayMaxPrice è il prezzo massimo reale convertito nella valuta selezionata
    const displayMaxPrice = useMemo(() => {
        const maxPriceToUse = actualMaxPriceEur;
        if (!rates?.rates) return maxPriceToUse;
        return Number(convertPrice(maxPriceToUse, currency, rates.rates));
    }, [actualMaxPriceEur, currency, rates]);

    // Converti i prezzi selezionati (in EUR) alla valuta corrente per la visualizzazione
    const selectedMinPrice = useMemo(() => {
        if (!rates?.rates) return selectedMinPriceEur;
        return Number(convertPrice(selectedMinPriceEur, currency, rates.rates));
    }, [selectedMinPriceEur, currency, rates]);

    const selectedPrice = useMemo(() => {
        if (!rates?.rates) return selectedPriceEur;
        return Number(convertPrice(selectedPriceEur, currency, rates.rates));
    }, [selectedPriceEur, currency, rates]);

    // Funzioni per aggiornare i prezzi: convertono dalla valuta corrente a EUR
    const setSelectedMinPrice: Dispatch<SetStateAction<number>> = value => {
        const numValue =
            typeof value === "function" ? value(selectedMinPrice) : value;
        if (!rates?.rates) {
            setSelectedMinPriceEur(numValue);
            return;
        }
        const eurValue = convertToEur(numValue, currency, rates.rates);
        setSelectedMinPriceEur(eurValue);
    };

    const setSelectedPrice: Dispatch<SetStateAction<number>> = value => {
        const numValue =
            typeof value === "function" ? value(selectedPrice) : value;
        // Marca che l'utente ha modificato il prezzo massimo
        setHasUserModifiedMaxPrice(true);
        if (!rates?.rates) {
            setSelectedPriceEur(numValue);
            return;
        }
        const eurValue = convertToEur(numValue, currency, rates.rates);
        setSelectedPriceEur(eurValue);
    };

    // Debounce dei prezzi per evitare troppe chiamate API durante il movimento dello slider
    const debouncedSelectedMinPriceEur = useDebounce(selectedMinPriceEur, 500);
    const debouncedSelectedPriceEur = useDebounce(selectedPriceEur, 500);

    // Helper: processa tourPriorities dalla risposta API
    const processTourPriorities = (
        tourPriorities: Array<{ tourId: string; priority: number }> | undefined,
        ids: string[],
        priorityById: Map<string, number>
    ) => {
        if (tourPriorities && tourPriorities.length > 0) {
            tourPriorities.forEach(({ tourId, priority }) => {
                priorityById.set(tourId, priority);
                ids.push(tourId);
            });
        }
    };

    // Helper: processa un singolo tour per fallback
    const processSingleTour = (
        tour: { _id?: string; title?: string; priority?: number },
        ids: string[],
        titles: string[],
        priorityById: Map<string, number>,
        priorityByTitle: Map<string, number>
    ) => {
        // eslint-disable-next-line no-underscore-dangle
        const tourId = tour?._id;
        if (tourId && !ids.includes(tourId)) {
            ids.push(tourId);
            if (
                !priorityById.has(tourId) &&
                typeof tour.priority === "number"
            ) {
                priorityById.set(tourId, tour.priority);
            }
        }
        const normalizedTitle = tour?.title?.toLowerCase().trim();
        if (normalizedTitle && normalizedTitle.length > 0) {
            titles.push(normalizedTitle);
            const hasPriority = typeof tour.priority === "number";
            const shouldSetPriority = !tourId || !priorityById.has(tourId);
            if (hasPriority && shouldSetPriority) {
                // TypeScript type narrowing: dopo il check hasPriority, tour.priority è garantito essere number
                const priorityValue = tour.priority as number;
                priorityByTitle.set(normalizedTitle, priorityValue);
            }
        }
    };

    // Helper: processa tour per fallback a tour.priority
    const processTourFallback = (
        tours:
            | Array<{ _id?: string; title?: string; priority?: number }>
            | undefined,
        ids: string[],
        titles: string[],
        priorityById: Map<string, number>,
        priorityByTitle: Map<string, number>
    ) => {
        tours?.forEach(tour => {
            processSingleTour(tour, ids, titles, priorityById, priorityByTitle);
        });
    };

    const tagDestinationReferences = useMemo(() => {
        if (!selectedTagDestination) {
            return {
                ids: [],
                titles: [] as string[],
                priorityById: new Map<string, number>(),
                priorityByTitle: new Map<string, number>(),
                tourPriorities: undefined as
                    | Array<{ tourId: string; priority: number }>
                    | undefined,
            };
        }
        const matchingTag = navBarData?.tagDestinations?.find(
            tag => tag.tagDestination === selectedTagDestination
        );
        const ids: string[] = [];
        const titles: string[] = [];
        const priorityById = new Map<string, number>();
        const priorityByTitle = new Map<string, number>();

        // Usa tourPriorities dalla risposta API se disponibile
        processTourPriorities(matchingTag?.tourPriorities, ids, priorityById);

        // Fallback a tour.priority se tourPriorities non è disponibile
        processTourFallback(
            matchingTag?.tour,
            ids,
            titles,
            priorityById,
            priorityByTitle
        );

        return {
            ids,
            titles,
            priorityById,
            priorityByTitle,
            tourPriorities: matchingTag?.tourPriorities,
        };
    }, [
        navBarData?.tagDestinations,
        selectedTagDestination,
        processTourFallback,
    ]);
    const shouldFilterByTag =
        !!selectedTagDestination &&
        (tagDestinationReferences.ids.length > 0 ||
            tagDestinationReferences.titles.length > 0);

    // Calcola il prezzo massimo da usare per la query: se l'utente non ha modificato il max, usa il max reale
    const maxPriceForQuery = useMemo(() => {
        // Se l'utente ha modificato il max, usa il valore selezionato
        // Altrimenti usa sempre il max reale per mostrare tutti i tour fino al prezzo massimo
        if (hasUserModifiedMaxPrice) {
            return debouncedSelectedPriceEur;
        }
        // Se non è stato modificato, usa sempre il max reale
        return actualMaxPriceEur;
    }, [hasUserModifiedMaxPrice, debouncedSelectedPriceEur, actualMaxPriceEur]);

    const { data: refreshedPackageData = [], isFetching } = useQuery({
        queryKey: [
            "package-data",
            selectedDestinations,
            selectedActivities,
            debouncedSelectedMinPriceEur,
            maxPriceForQuery,
            selectedTourType,
            tourOptionToFilter,
            selectedTagDestination,
            currency, // Include currency in queryKey per invalidare cache quando cambia valuta
        ],
        queryFn: () =>
            getFilterPackageData(
                selectedDestinations,
                selectedActivities,
                debouncedSelectedMinPriceEur,
                maxPriceForQuery,
                selectedTourType,
                tourOptionToFilter,
                selectedTagDestination ?? undefined
            ),
        initialData: () => packageData,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const filteredPackageData = useMemo(() => {
        // Assicurati che refreshedPackageData sia sempre un array
        const data = Array.isArray(refreshedPackageData)
            ? refreshedPackageData
            : [];

        if (!shouldFilterByTag) {
            return data;
        }

        // Helper: estrae gli ID da un tour
        const getTourIds = (tour: SearchResponseTO): string[] => {
            // eslint-disable-next-line no-underscore-dangle
            const qualityId = tour?.quality?._id;
            // eslint-disable-next-line no-underscore-dangle
            const premiumId = tour?.premium?._id;
            return [
                qualityId,
                premiumId,
                tour?.quality?.tourId,
                tour?.premium?.tourId,
            ].filter((id): id is string => Boolean(id));
        };

        // Helper: estrae le priorità dagli ID
        const getPrioritiesFromIds = (ids: string[]): number[] => {
            const priorities: number[] = [];
            ids.forEach(id => {
                const value = tagDestinationReferences.priorityById.get(id);
                if (typeof value === "number") {
                    priorities.push(value);
                }
            });
            return priorities;
        };

        // Helper: estrae le priorità dai titoli
        const getPrioritiesFromTitles = (titles: string[]): number[] => {
            const priorities: number[] = [];
            titles.forEach(title => {
                const value =
                    tagDestinationReferences.priorityByTitle.get(title);
                if (typeof value === "number") {
                    priorities.push(value);
                }
            });
            return priorities;
        };

        const getPackagePriority = (tour: SearchResponseTO) => {
            const idsToCheck = getTourIds(tour);
            const priorities: number[] = getPrioritiesFromIds(idsToCheck);

            // Se non abbiamo tourPriorities, usa anche i titoli come fallback
            if (
                !tagDestinationReferences.tourPriorities ||
                tagDestinationReferences.tourPriorities.length === 0
            ) {
                const titlesToCheck = [
                    tour?.quality?.title?.toLowerCase().trim(),
                    tour?.premium?.title?.toLowerCase().trim(),
                ].filter((title): title is string => Boolean(title));
                priorities.push(...getPrioritiesFromTitles(titlesToCheck));
            }

            if (priorities.length === 0) return null;
            return Math.min(...priorities);
        };

        const filtered = data.filter(tour => {
            // eslint-disable-next-line no-underscore-dangle
            const qualityId = tour?.quality?._id;
            // eslint-disable-next-line no-underscore-dangle
            const premiumId = tour?.premium?._id;
            const idsToCheck = [
                qualityId,
                premiumId,
                tour?.quality?.tourId,
                tour?.premium?.tourId,
            ].filter((id): id is string => Boolean(id));
            const titlesToCheck = [
                tour?.quality?.title?.toLowerCase().trim(),
                tour?.premium?.title?.toLowerCase().trim(),
            ].filter((title): title is string => Boolean(title));

            const matchById = idsToCheck.some(id =>
                tagDestinationReferences.ids.includes(id)
            );
            const matchByTitle = titlesToCheck.some(title =>
                tagDestinationReferences.titles.includes(title)
            );
            return matchById || matchByTitle;
        });

        // Ordina in decrescente (priorità più alta prima)
        return sortByPriority(tour => {
            const priority = getPackagePriority(tour);
            return priority !== null ? -priority : null; // Inverti il segno per ordinamento decrescente
        }, filtered);
    }, [
        refreshedPackageData,
        shouldFilterByTag,
        tagDestinationReferences.ids,
        tagDestinationReferences.titles,
        tagDestinationReferences.priorityById,
        tagDestinationReferences.priorityByTitle,
        tagDestinationReferences.tourPriorities,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ]);

    const [sortingSelect, setSortingSelect] = useState<boolean>(false);
    const [sortingMethod, setSortingMethod] = useState<SearchSort | undefined>(
        SearchSort.RECOMMENDED
    );

    // Query separata per ottenere i tour filtrati solo per destinazioni, tour type e prezzo (senza attività)
    // Questo serve per estrarre le attività disponibili dai tour filtrati
    const { data: toursForActivitiesData = [] } = useQuery({
        queryKey: [
            "tours-for-activities",
            selectedDestinations,
            debouncedSelectedMinPriceEur,
            maxPriceForQuery,
            selectedTourType,
            tourOptionToFilter,
            selectedTagDestination,
            currency,
        ],
        queryFn: () =>
            getFilterPackageData(
                selectedDestinations,
                [], // Nessuna attività nel filtro
                debouncedSelectedMinPriceEur,
                maxPriceForQuery,
                selectedTourType,
                tourOptionToFilter,
                selectedTagDestination ?? undefined
            ),
        initialData: () => packageData,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    // Tour filtrati senza considerare le attività (per estrarre le attività disponibili)
    const toursForActivities = useMemo(() => {
        if (!toursForActivitiesData || toursForActivitiesData.length === 0) {
            return packageData;
        }

        // Applica il filtro per tag destination se necessario
        if (!shouldFilterByTag) {
            return toursForActivitiesData;
        }
        // Applica lo stesso filtro per tag destination
        return toursForActivitiesData.filter(tour => {
            const idsToCheck = [
                // eslint-disable-next-line no-underscore-dangle
                tour?.quality?._id,
                // eslint-disable-next-line no-underscore-dangle
                tour?.premium?._id,
                tour?.quality?.tourId,
                tour?.premium?.tourId,
            ].filter((id): id is string => Boolean(id));
            const titlesToCheck = [
                tour?.quality?.title?.toLowerCase().trim(),
                tour?.premium?.title?.toLowerCase().trim(),
            ].filter((title): title is string => Boolean(title));

            const matchById = idsToCheck.some(id =>
                tagDestinationReferences.ids.includes(id)
            );
            const matchByTitle = titlesToCheck.some(title =>
                tagDestinationReferences.titles.includes(title)
            );
            return matchById || matchByTitle;
        });
    }, [
        toursForActivitiesData,
        packageData,
        shouldFilterByTag,
        tagDestinationReferences,
    ]);

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const sortedPackageData = useMemo(() => {
        // se tourOptionToFilter è undefined (quindi stiamo cercando tutti i tour), prendiamo in considerazione i quality
        if (
            !Array.isArray(filteredPackageData) ||
            filteredPackageData.length === 0
        ) {
            return [];
        }
        return _.cloneDeep(filteredPackageData).sort((a, b) => {
            const first =
                tourOptionToFilter === "premium" ? a.premium : a.quality;
            const second =
                tourOptionToFilter === "premium" ? b.premium : b.quality;
            switch (sortingMethod) {
                case SearchSort.RECOMMENDED: {
                    // Converte rating da string a number, gestisce valori null/undefined/empty/NaN
                    const firstRatingNum = first.rating
                        ? parseFloat(first.rating)
                        : NaN;
                    const secondRatingNum = second.rating
                        ? parseFloat(second.rating)
                        : NaN;

                    // Verifica se i rating sono validi (maggiore di 0 e non NaN)
                    const firstHasRating =
                        !Number.isNaN(firstRatingNum) && firstRatingNum > 0;
                    const secondHasRating =
                        !Number.isNaN(secondRatingNum) && secondRatingNum > 0;

                    // Se entrambi hanno rating valido, ordina dalla più alta alla più bassa
                    if (firstHasRating && secondHasRating) {
                        return secondRatingNum - firstRatingNum;
                    }
                    // Se solo il primo ha rating valido, viene prima
                    if (firstHasRating && !secondHasRating) {
                        return -1;
                    }
                    // Se solo il secondo ha rating valido, viene prima
                    if (!firstHasRating && secondHasRating) {
                        return 1;
                    }
                    // Se nessuno ha rating valido, mantieni l'ordine originale
                    return 0;
                }
                case SearchSort.LARGE_TO_LOW:
                    return (
                        +(second.startingPrice ?? second.price) -
                        +(first.startingPrice ?? first.price)
                    );
                case SearchSort.LOW_TO_LARGE:
                    return (
                        +(first.startingPrice ?? first.price) -
                        +(second.startingPrice ?? second.price)
                    );
                default:
                    return 0;
            }
        });
    }, [filteredPackageData, sortingMethod, tourOptionToFilter]);

    const [basicModal, setBasicModal] = useState(false);

    const toggleShow = () => setBasicModal(!basicModal);

    useEffect(() => {
        setNumberFilteredPackage(sortedPackageData?.length ?? 0);
    }, [sortedPackageData, setNumberFilteredPackage]);

    useEffect(() => {
        if (sortedPackageData?.length > 0) {
            TagManager.dataLayer(
                viewItemList(
                    "tours-destinations",
                    "Pagina Tours",
                    sortedPackageData?.map((tour, index) =>
                        createGA4ItemFromTour(
                            "tours-destinations",
                            "Pagina Tours",
                            tour,
                            index
                        )
                    )
                )
            );
        }
    }, [sortedPackageData]);

    return (
        <>
            <MDBContainer className="px-lg-5 pe-0">
                <MDBRow>
                    {useIsMD() && (
                        <MDBCol
                            className="d-flex justify-content-center py-3 px-4"
                            size={12}
                            md={12}
                        >
                            <MDBBtn
                                className={styles.filterActionButton}
                                onClick={toggleShow}
                            >
                                <div className="pe-1 pt-1">
                                    <shoppingSvg.FilterLine />
                                </div>
                                Filter the tours
                            </MDBBtn>
                        </MDBCol>
                    )}
                    <MDBCol
                        className="d-flex justify-content-center py-3 px-4"
                        md={8}
                        xxl={10}
                    >
                        <TourDestinationsFiltersRecap
                            minPrice={+filtersData.minPrice}
                            maxPrice={+filtersData.maxPrice}
                            selectedDestinations={selectedDestinations}
                            selectedActivities={selectedActivities}
                            selectedPrice={selectedPrice}
                            selectedMinPrice={selectedMinPrice}
                            selectedTourType={selectedTourType}
                            setSelectedDestinations={setSelectedDestinations}
                            setSelectedActivities={setSelectedActivities}
                            setSelectedPrice={setSelectedPrice}
                            setSelectedMinPrice={setSelectedMinPrice}
                            setSelectedTourType={setSelectedTourType}
                            tourOption={tourOptionToFilter}
                            setTourOption={setTourOptionToFilter}
                            selectedTagDestination={selectedTagDestination}
                            setSelectedTagDestination={
                                setSelectedTagDestination
                            }
                            tagDestinations={navBarData?.tagDestinations?.map(
                                tag => ({
                                    tagDestination: tag.tagDestination,
                                    title: tag.title,
                                })
                            )}
                        />
                    </MDBCol>
                    <MDBCol
                        className="d-flex py-3 px-4 justify-content-center align-content-center position-relative"
                        md={4}
                        xxl={2}
                    >
                        <div
                            className={styles.sortingSelect}
                            onClick={() => setSortingSelect(!sortingSelect)}
                        >
                            <p>{sortingMethod ?? "Choose Filter"}</p>
                            <span>
                                {sortingSelect ? (
                                    <userInterfaceSvg.ArrowUpSimpleLine />
                                ) : (
                                    <userInterfaceSvg.ArrowDownSimpleLine />
                                )}
                            </span>
                        </div>
                        {sortingSelect ? (
                            <div className={styles.sortingOptionContainer}>
                                <div
                                    className={
                                        styles.sortingOptionInnerContainer
                                    }
                                >
                                    {(
                                        Object.keys(SearchSort) as Array<
                                            keyof typeof SearchSort
                                        >
                                    ).map(el => (
                                        <div
                                            className={
                                                sortingMethod ===
                                                SearchSort[
                                                    el as keyof typeof SearchSort
                                                ]
                                                    ? styles.sortingOptionSelected
                                                    : styles.sortingOption
                                            }
                                            key={randomUid()}
                                        >
                                            <button
                                                type="button"
                                                className="my-3 btn btn-link p-0 text-start w-100"
                                                onClick={() => {
                                                    const searchSortEl =
                                                        SearchSort[
                                                            el as keyof typeof SearchSort
                                                        ];
                                                    setSortingMethod(
                                                        sortingMethod ===
                                                            searchSortEl
                                                            ? undefined
                                                            : searchSortEl
                                                    );
                                                    setSortingSelect(false);
                                                }}
                                                onKeyDown={e => {
                                                    if (
                                                        e.key === "Enter" ||
                                                        e.key === " "
                                                    ) {
                                                        e.preventDefault();
                                                        const searchSortEl =
                                                            SearchSort[
                                                                el as keyof typeof SearchSort
                                                            ];
                                                        setSortingMethod(
                                                            sortingMethod ===
                                                                searchSortEl
                                                                ? undefined
                                                                : searchSortEl
                                                        );
                                                        setSortingSelect(false);
                                                    }
                                                }}
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                            >
                                                {
                                                    SearchSort[
                                                        el as keyof typeof SearchSort
                                                    ]
                                                }
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </MDBCol>
                </MDBRow>
            </MDBContainer>
            <MDBContainer className="px-lg-5 px-0">
                <MDBRow>
                    {!useIsMD() && (
                        <MDBCol
                            className={` ${styles.filetrBox} d-flex justify-content-center`}
                            size={4}
                        >
                            <TourDestinationsFilters
                                selectedDestinations={selectedDestinations}
                                selectedActivities={selectedActivities}
                                selectedPrice={selectedPrice}
                                selectedTourType={selectedTourType}
                                setSelectedDestinations={
                                    setSelectedDestinations
                                }
                                setSelectedActivities={setSelectedActivities}
                                setSelectedPrice={setSelectedPrice}
                                setSelectedTourType={setSelectedTourType}
                                tourOption={tourOptionToFilter}
                                setTourOption={setTourOptionToFilter}
                                filtersData={filtersData}
                                onResetFilters={() => {
                                    setSelectedTagDestination(null);
                                    // Reset del flag quando si resettano i filtri
                                    setHasUserModifiedMaxPrice(false);
                                    isInitializingRef.current = true;
                                }}
                                hasTagDestinationFilter={
                                    !!selectedTagDestination
                                }
                                selectedMinPrice={selectedMinPrice}
                                setSelectedMinPrice={setSelectedMinPrice}
                                displayMinPrice={displayMinPrice}
                                displayMaxPrice={displayMaxPrice}
                                onPriceRangeChange={() => {
                                    // Questo callback viene chiamato quando cambia il range
                                    // setSelectedPrice già imposta hasUserModifiedMaxPrice, quindi questo è solo un fallback
                                }}
                                availableTours={toursForActivities}
                                tagDestinations={navBarData?.tagDestinations}
                                selectedTagDestination={selectedTagDestination}
                                setSelectedTagDestination={
                                    setSelectedTagDestination
                                }
                            />
                        </MDBCol>
                    )}
                    <MDBCol
                        className={` ${styles.filetrBox} d-flex flex-column`}
                        size={12}
                        lg={8}
                    >
                        {/* eslint-disable no-underscore-dangle */}
                        {isFetching ? (
                            <div className="d-flex w-100 h-100 justify-content-center align-items-center">
                                <BounceLoader color="#ed6f6f" size={150} />
                            </div>
                        ) : (sortedPackageData ?? []).length > 0 ? (
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            sortedPackageData!.map((el, key) => (
                                <TourDestinationsCard
                                    cardData={el}
                                    key={
                                        el?.quality?._id ||
                                        el?.premium?._id ||
                                        key
                                    }
                                    premiumGlobal={
                                        !!el?.onlyPremium ||
                                        (!el?.onlyPremium &&
                                            !el.onlyQuality &&
                                            tourOptionToFilter !== undefined &&
                                            tourOptionToFilter !== "quality")
                                    }
                                />
                            ))
                        ) : (
                            <ToursDestinationsNoResults />
                        )}
                        {/* eslint-enable */}
                    </MDBCol>
                </MDBRow>
            </MDBContainer>
            {useIsMD() && (
                <TourDestinationsModalFilters
                    show={basicModal}
                    setShow={setBasicModal}
                >
                    <TourDestinationsFilters
                        selectedDestinations={selectedDestinations}
                        selectedActivities={selectedActivities}
                        selectedPrice={selectedPrice}
                        selectedTourType={selectedTourType}
                        setSelectedDestinations={setSelectedDestinations}
                        setSelectedActivities={setSelectedActivities}
                        setSelectedPrice={setSelectedPrice}
                        setSelectedTourType={setSelectedTourType}
                        tourOption={tourOptionToFilter}
                        setTourOption={setTourOptionToFilter}
                        filtersData={filtersData}
                        onResetFilters={() => {
                            setSelectedTagDestination(null);
                            // Reset del flag quando si resettano i filtri
                            setHasUserModifiedMaxPrice(false);
                            isInitializingRef.current = true;
                        }}
                        hasTagDestinationFilter={!!selectedTagDestination}
                        selectedMinPrice={selectedMinPrice}
                        setSelectedMinPrice={setSelectedMinPrice}
                        displayMinPrice={displayMinPrice}
                        displayMaxPrice={displayMaxPrice}
                        onPriceRangeChange={() => {
                            // Questo callback viene chiamato quando cambia il range
                            // setSelectedPrice già imposta hasUserModifiedMaxPrice, quindi questo è solo un fallback
                        }}
                        availableTours={toursForActivities}
                        tagDestinations={navBarData?.tagDestinations}
                        selectedTagDestination={selectedTagDestination}
                        setSelectedTagDestination={setSelectedTagDestination}
                    />
                </TourDestinationsModalFilters>
            )}
        </>
    );
};

export default ToursDestinationsSearch;
